// src/hooks/use-pto.ts
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type { PtoApprovalScope } from "@/lib/enums";
import type { PendingCounts } from "@/lib/pto/alerts";
import type { PtoStatus } from "@/lib/pto/approval-rules";
import { deleteToast } from "@/lib/pto/my-requests";
import { runUncertain, type UncertainAction } from "@/lib/pto/uncertain";
import { trackCreate } from "@/lib/pto/uncertain-create";
import { monthKeys, ptoKeys, ptoWriteKeys, teamKeys, timeKeys } from "@/lib/query";
import { ApiError, getJson, sendJson } from "@/lib/time/api-client";
import type {
  ApprovalsView,
  AskCancelInput,
  BalancesView,
  MyRequestsView,
  QuoteQuery,
  QuoteView,
  RequestIdInput,
  RequestIdResult,
  RequestInput,
  RequestWriteResult,
  UpdateRequestInput,
} from "@/schemas/pto";
import type {
  PtoBookResult,
  PtoDecisionInput,
  PtoDecisionResult,
  PtoRejectInput,
} from "@/schemas/team";

export function useBalances(year: number, enabled = true) {
  return useQuery({
    queryKey: ptoKeys.balances(year),
    queryFn: () => getJson<BalancesView>(`/api/pto/balances?year=${year}`),
    enabled,
  });
}

export function useMyRequests(year: number) {
  return useQuery({
    queryKey: ptoKeys.requests(year),
    queryFn: () => getJson<MyRequestsView>(`/api/pto/requests?year=${year}`),
  });
}

function quoteParams(q: QuoteQuery): string {
  const p = new URLSearchParams({
    typeId: q.typeId,
    start: q.start,
    end: q.end,
    hours: String(q.hours),
  });
  if (q.excludeId) p.set("excludeId", q.excludeId);
  return p.toString();
}

/**
 * The dialog's live quote, fetched as soon as the query changes: every input it reads (date cells,
 * type, day length, hours) is a click, not typing, so there is nothing to debounce (the free-text
 * note is not part of the quote). Keeps the last answer on screen while the next one loads.
 */
export function useQuote(q: QuoteQuery | null) {
  const key = q ? quoteParams(q) : "";
  return useQuery({
    queryKey: ptoKeys.quote(key),
    queryFn: () => getJson<QuoteView>(`/api/pto/quote?${key}`),
    enabled: q !== null,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}

/** My Team's ed-pto-pending-count for this person; refreshed every 5 minutes for the badge. */
export function usePendingCounts() {
  return useQuery({
    queryKey: ptoKeys.hrPending(),
    queryFn: () => getJson<PendingCounts>("/api/pto/pending"),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useApprovals(scope: PtoApprovalScope, enabled: boolean) {
  return useQuery({
    queryKey: ptoKeys.approvals(scope),
    queryFn: () => getJson<ApprovalsView>(`/api/pto/approvals?scope=${scope}`),
    enabled,
  });
}

/**
 * A PTO write moves balances, lists and counts (pto), My Team's panel, timeline and pending
 * approvals (team: the banner and the nav badge), the month totals on Home, and booked days in
 * the time weeks, together.
 */
export function invalidatePtoWrite(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ptoKeys.all });
  void qc.invalidateQueries({ queryKey: teamKeys.all });
  void qc.invalidateQueries({ queryKey: monthKeys.all });
  void qc.invalidateQueries({ queryKey: timeKeys.all });
}

interface MutationOptions<I, R> {
  /** Writes that may have saved on a 504: a failed retry is judged by the request's real state. */
  settle?: { action: UncertainAction; idOf: (input: I) => string };
  /** A send of its own in place of the plain one (the create holds itself while it is out). */
  send?: (input: I) => Promise<R>;
  /** Lets a dialog see the write is in flight (and not close under it). */
  mutationKey?: readonly unknown[];
}

/** The mutation function: a plain write, or one that settles an uncertain earlier attempt. */
export async function sendPto<I, R>(
  method: "POST" | "PUT",
  url: string,
  input: I,
  settle?: MutationOptions<I, R>["settle"],
): Promise<R> {
  if (!settle) return sendJson<R>(method, url, input);
  const id = settle.idOf(input);
  const run = await runUncertain(settle.action, id, () => sendJson<R>(method, url, input));
  // The first attempt landed: report the same shape a success would.
  return run.settled ? ({ id } as R) : run.result;
}

function usePtoMutation<I, R>(
  method: "POST" | "PUT",
  url: string,
  success: (result: R, input: I) => string,
  options: MutationOptions<I, R> = {},
) {
  const qc = useQueryClient();
  const { settle, send, mutationKey } = options;
  return useMutation({
    mutationKey,
    // Never auto-retry a write: a repeat could land twice. The user's own retry is idempotent.
    retry: false,
    mutationFn: async (input: I) => {
      return send ? send(input) : sendPto<I, R>(method, url, input, settle);
    },
    onSuccess: (result, input) => {
      toast.success(success(result, input));
      invalidatePtoWrite(qc);
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      invalidatePtoWrite(qc);
    },
  });
}

const byId = (input: { id: string }) => input.id;

/**
 * The create's send. It holds the new request's id and fields itself while the send is out and
 * after a timeout, so the hold survives the dialog closing before the answer arrives.
 */
export function sendCreateRequest(input: RequestInput): Promise<RequestWriteResult> {
  const { id, description, ...rest } = input;
  return trackCreate(id, { ...rest, description: description ?? "" }, () =>
    sendJson<RequestWriteResult>("POST", "/api/pto/requests", input),
  );
}

export const useCreateRequest = () =>
  usePtoMutation<RequestInput, RequestWriteResult>(
    "POST",
    "/api/pto/requests",
    () => "Request sent",
    { send: sendCreateRequest, mutationKey: ptoWriteKeys.request() },
  );
export const useUpdateRequest = () =>
  usePtoMutation<UpdateRequestInput, RequestWriteResult>(
    "PUT",
    "/api/pto/requests",
    () => "Request sent",
    { mutationKey: ptoWriteKeys.request() },
  );
/** A delete, with the row's status for its toast (only the id is sent). */
export type DeleteRequestInput = RequestIdInput & { status: PtoStatus };

export const sendDeleteRequest = ({ id }: DeleteRequestInput) =>
  sendPto<RequestIdInput, RequestIdResult>(
    "POST",
    "/api/pto/requests/delete",
    { id },
    {
      action: "delete",
      idOf: byId,
    },
  );

export const useDeleteRequest = () =>
  usePtoMutation<DeleteRequestInput, RequestIdResult>(
    "POST",
    "/api/pto/requests/delete",
    (_result, input) => deleteToast(input.status),
    { send: sendDeleteRequest },
  );
export const useAskCancel = () =>
  usePtoMutation<AskCancelInput, RequestIdResult>(
    "POST",
    "/api/pto/requests/ask-cancel",
    () => "Cancel asked",
    { settle: { action: "askCancel", idOf: byId } },
  );
export const useApprovePtoAny = () =>
  usePtoMutation<PtoDecisionInput, PtoDecisionResult>("POST", "/api/pto/approvals/approve", (r) =>
    r.bookFailed ? "Approved. Time not booked yet." : "Approved",
  );
export const useBookPtoAny = () =>
  usePtoMutation<PtoDecisionInput, PtoBookResult>(
    "POST",
    "/api/pto/approvals/book",
    () => "Time booked",
  );
export const useRejectPtoAny = () =>
  usePtoMutation<PtoRejectInput, PtoDecisionResult>(
    "POST",
    "/api/pto/approvals/reject",
    () => "Rejected",
  );
/** The approver's remove, settled by the request's real state after a 504, like Keep. */
export const sendRemovePto = (input: RequestIdInput) =>
  sendPto<RequestIdInput, RequestIdResult>("POST", "/api/pto/approvals/remove", input, {
    action: "remove",
    idOf: byId,
  });

export const useRemovePto = () =>
  usePtoMutation<RequestIdInput, RequestIdResult>(
    "POST",
    "/api/pto/approvals/remove",
    () => "Removed",
    { send: sendRemovePto },
  );
export const useKeepPto = () =>
  usePtoMutation<RequestIdInput, RequestIdResult>("POST", "/api/pto/approvals/keep", () => "Kept", {
    settle: { action: "keep", idOf: byId },
  });
